import { captureException } from "@sentry/vue";

/**
 * Safely parses postMessage data, handling both objects and JSON strings
 * @param {any} data - The data received from postMessage
 * @returns {object|null} - Parsed object or null if parsing fails
 */
export function safeParsePostMessage(data) {
  // If data is already an object (and not null), return it directly
  if (typeof data === 'object' && data !== null) {
    return data;
  }

  // If data is a string, try to parse it as JSON
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      // Ensure the parsed result is an object (not a number, string, etc.)
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch (e) {
      console.warn('Failed to parse postMessage data as JSON:', e);
    }
  }

  // Return null for any other cases (invalid JSON, non-object, etc.)
  return null;
}

/**
 * Enhanced version with type checking
 * @param {any} data - The data received from postMessage
 * @param {string} expectedType - Optional type to validate against
 * @returns {object|null} - Parsed object or null if parsing fails or type doesn't match
 */
export function safeParseAndValidatePostMessage(data, expectedType) {
  const parsed = safeParsePostMessage(data);
  if (!parsed) return null;

  if (expectedType && parsed.type !== expectedType) {
    console.warn(`Expected postMessage type ${expectedType} but got ${parsed.type}`);
    return null;
  }

  return parsed;
}

/**
 * Creates a postMessage channel with retry and exponential backoff
 * @param {Window} iframeWindow The target window to post messages to
 * @param {string} targetOrigin The target origin for postMessage
 * @param {object} request The message request payload
 * @param {function} responseValidator Function to validate the response
 * @param {object} [options] Configuration options
 * @param {number} [options.maxRetries=3]
 * @param {number} [options.initialDelay=2000]
 * @param {number} [options.timeout=2000]
 * @returns {Promise} Promise with the response or null if all retries fail
 */
export const createPostMessageChannel = (
  iframeWindow,
  targetOrigin,
  request,
  responseValidator,
  options = {}
) => {
  const {
    maxRetries = 3,
    initialDelay = 2000,
    timeout = 2000
  } = options;

  return new Promise((resolve) => {
    let retryCount = 0;
    let timeoutId;
    let listener;

    const cleanup = () => {
      window.removeEventListener('message', listener);
      clearTimeout(timeoutId);
    };

    const attempt = () => {
      cleanup();

      listener = (event) => {
        try {
          const msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (responseValidator(msg)) {
            cleanup();
            resolve(msg);
          }
        } catch (e) {
          console.error('Error processing message:', e);
          retryOrGiveUp();
        }
      };

      window.addEventListener('message', listener);

      iframeWindow.postMessage(request, targetOrigin);

      const retryOrGiveUp = () => {
        retryCount++;
        if (retryCount < maxRetries) {
          const delay = initialDelay * Math.pow(2, retryCount - 1);
          console.log(`Retrying (${retryCount}/${maxRetries}) in ${delay}ms...`);
          timeoutId = setTimeout(attempt, delay);
        } else {
          cleanup();
          console.error(`Max retries (${maxRetries}) exceeded`);
          resolve(null);
          captureException(
            Error(`Max retries exceeded`),
            {
              level: "error",
              extra: {
                targetOrigin: targetOrigin,
                request: request,
                options: {
                  maxRetries: maxRetries,
                  initialDelay: initialDelay,
                  timeout: timeout,
                },
              }
            }
          );
        }
      };

      timeoutId = setTimeout(retryOrGiveUp, timeout);
    };

    attempt();
  });
};
